/**
 * Regelbasierte Hinweise auf weitere Untersuchungen (Auftrag F08, Abschnitt 9).
 * Jede Regel nennt die auslösende Kennzahl mit Zeitraum, die Bedingung und die
 * für eine vertiefte Beurteilung nötigen Gebäudedaten. Schwellen sind
 * konfigurierbare Projektannahmen, keine Normanforderungen.
 */
import type { AnalysisConfig, BuildingResult, MetricKey, YearMetrics } from '../domain/types';

export interface HintTrigger {
  metric: MetricKey;
  value: number;
  unit: string;
  period: string;
  reference?: { value: number; period: string };
}

export interface Hint {
  id: string;
  title: string;
  tone: 'warm' | 'cool' | 'neutral';
  trigger: HintTrigger;
  condition: string;
  needs: string[];
}

export interface HintRule {
  id: string;
  title: string;
  tone: Hint['tone'];
  needs: string[];
  describe: (c: AnalysisConfig) => string;
  evaluate: (ctx: HintContext) => HintTrigger | null;
}

export interface HintContext {
  cfg: AnalysisConfig;
  recent: YearMetrics[];
  recentLabel: string;
  refMeans: BuildingResult['reference']['means'] | null;
  refLabel: string;
}

const mean = (ys: YearMetrics[], k: MetricKey) => ys.reduce((a, y) => a + (y[k] as number), 0) / ys.length;

export const HINT_RULES: HintRule[] = [
  {
    id: 'sommerlicher-waermeschutz',
    title: 'Vertiefte Untersuchung des sommerlichen Wärmeschutzes sinnvoll',
    tone: 'warm',
    needs: ['Fensterflächen und Orientierung', 'Sonnenschutz (g-Wert, Steuerung)', 'Speichermasse', 'Interne Lasten und Nutzungszeiten', 'Lüftungskonzept'],
    describe: (c) => `Ø Kühlgradtage ≥ ${c.hints.cddRecentMin} K·d oder Ø heisse Tage ≥ ${c.hints.hotDaysRecentMin} pro Jahr`,
    evaluate: ({ cfg, recent, recentLabel }) => {
      const cdd = mean(recent, 'cdd');
      if (cdd >= cfg.hints.cddRecentMin) return { metric: 'cdd', value: cdd, unit: 'K·d', period: recentLabel };
      const hot = mean(recent, 'hotDays');
      if (hot >= cfg.hints.hotDaysRecentMin) return { metric: 'hotDays', value: hot, unit: 'Tage', period: recentLabel };
      return null;
    },
  },
  {
    id: 'nachtauskuehlung-potenzial',
    title: 'Viele Nächte bieten rechnerisch eine Temperaturdifferenz für Nachtauskühlung',
    tone: 'cool',
    needs: ['Öffenbare Fensterflächen und Lüftungsquerschnitte', 'Einbruch-, Witterungs- und Lärmschutz', 'Speichermasse', 'Betriebszeiten und Luftwechsel'],
    describe: (c) => `Anteil geeigneter Nachtstunden in der Saison ≥ ${Math.round(c.hints.coolShareMin * 100)} %`,
    evaluate: ({ cfg, recent, recentLabel }) => {
      const hours = recent.reduce((a, y) => a + y.coolHours, 0);
      const windowHours = recent.reduce((a, y) => a + y.seasonNightHours, 0);
      if (windowHours <= 0) return null;
      const share = hours / windowHours;
      if (share >= cfg.hints.coolShareMin) return { metric: 'coolHours', value: share * 100, unit: '% der Nachtstunden', period: recentLabel };
      return null;
    },
  },
  {
    id: 'warme-naechte',
    title: 'Warme Nächte schränken die Nachtauskühlung zeitweise ein',
    tone: 'warm',
    needs: ['Raumtemperaturmessungen in Sommernächten', 'Nutzung von Schlaf- und Aufenthaltsräumen', 'Mechanische Kühl- oder Lüftungsoptionen'],
    describe: (c) => `Ø warme Nächte ≥ ${c.hints.warmNightsRecentMin} pro Jahr`,
    evaluate: ({ cfg, recent, recentLabel, refMeans, refLabel }) => {
      const wn = mean(recent, 'warmNights');
      if (wn >= cfg.hints.warmNightsRecentMin)
        return { metric: 'warmNights', value: wn, unit: 'Nächte', period: recentLabel, reference: refMeans?.warmNights !== undefined ? { value: refMeans.warmNights, period: refLabel } : undefined };
      return null;
    },
  },
  {
    id: 'hitzeperioden',
    title: 'Robustheit bei mehrtägiger Hitze prüfen',
    tone: 'warm',
    needs: ['Dynamische Gebäudesimulation mit Extremperiode', 'Nutzungsprofil und interne Lasten', 'Speichermasse und Nachtlüftung'],
    describe: (c) => `Längste Hitzeperiode im Zeitraum ≥ ${c.hints.heatLongestMin} Tage`,
    evaluate: ({ cfg, recent, recentLabel }) => {
      const longest = Math.max(0, ...recent.map((y) => y.heatLongest));
      if (longest >= cfg.hints.heatLongestMin) return { metric: 'heatLongest', value: longest, unit: 'Tage', period: recentLabel.replace(/^Ø/, 'Maximum') };
      return null;
    },
  },
  {
    id: 'heizgradtage-rueckgang',
    title: 'Auslegungs- und Verbrauchskennwerte mit aktuellen Klimadaten abgleichen',
    tone: 'neutral',
    needs: ['Gemessener Heizenergieverbrauch', 'Heizgrenze und Heizkurve', 'Auslegungsdaten der Wärmeerzeugung'],
    describe: (c) => `Ø Heizgradtage mindestens ${Math.round(c.hints.hddDropMin * 100)} % unter der Referenz`,
    evaluate: ({ cfg, recent, recentLabel, refMeans, refLabel }) => {
      if (!refMeans || refMeans.hdd === undefined || refMeans.hdd === 0) return null;
      const hdd = mean(recent, 'hdd');
      if (hdd <= refMeans.hdd * (1 - cfg.hints.hddDropMin)) return { metric: 'hdd', value: hdd, unit: 'K·d', period: recentLabel, reference: { value: refMeans.hdd, period: refLabel } };
      return null;
    },
  },
  {
    id: 'kuehlgradtage-anstieg',
    title: 'Kühlstrategie und Anlagenkapazität überprüfen',
    tone: 'warm',
    needs: ['Kühllastberechnung', 'Vorhandene Kälte- und Lüftungsanlagen', 'Anforderungen an Raumtemperatur und Nutzung'],
    describe: (c) => `Ø Kühlgradtage mindestens ${Math.round(c.hints.cddRiseMin * 100)} % über der Referenz und ≥ 10 K·d`,
    evaluate: ({ cfg, recent, recentLabel, refMeans, refLabel }) => {
      if (!refMeans || refMeans.cdd === undefined || refMeans.cdd <= 0) return null;
      const cdd = mean(recent, 'cdd');
      if (cdd >= 10 && cdd >= refMeans.cdd * (1 + cfg.hints.cddRiseMin)) return { metric: 'cdd', value: cdd, unit: 'K·d', period: recentLabel, reference: { value: refMeans.cdd, period: refLabel } };
      return null;
    },
  },
];

export interface HintEvaluation {
  hints: Hint[];
  recentYears: number[];
  sufficient: boolean;
  reason?: string;
}

export function evaluateHints(result: BuildingResult, cfg: AnalysisConfig): HintEvaluation {
  const complete = result.years.filter((y) => y.complete && y.year >= cfg.periodStart && y.year <= cfg.periodEnd);
  const recent = complete.slice(-cfg.hints.recentYears);
  const minYears = Math.ceil(cfg.hints.recentYears / 2);
  if (recent.length < minYears) {
    return { hints: [], recentYears: recent.map((y) => y.year), sufficient: false, reason: `Weniger als ${minYears} vollständige Jahre im gewählten Zeitraum` };
  }
  const recentLabel = `Ø ${recent[0].year}–${recent[recent.length - 1].year} (${recent.length} vollständige Jahre)`;
  const refMeans = result.reference.status === 'ungenuegend' ? null : result.reference.means;
  const refLabel = `Mittel ${result.reference.start}–${result.reference.end}${result.reference.status === 'teil' ? ' (Teilreferenz)' : ''}`;
  const ctx: HintContext = { cfg, recent, recentLabel, refMeans, refLabel };
  const hints: Hint[] = [];
  for (const r of HINT_RULES) {
    const trigger = r.evaluate(ctx);
    if (trigger) hints.push({ id: r.id, title: r.title, tone: r.tone, trigger, condition: r.describe(cfg), needs: r.needs });
  }
  return { hints, recentYears: recent.map((y) => y.year), sufficient: true };
}
